import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import AppText from "@/components/AppText";
import AppBox from "@/components/AppBox";
import AppSwitch from "../AppSwitch";
import DynamicInputList from "@/components/DynamicInputList";
import { usersUpdateUserNotAdmin } from "@/store/users";
import Cookies from "universal-cookie";

interface Props {
  config: any;
  onConfigChange?: (field: string, value: any) => void;
}

const AnsweredCallReplies: React.FC<Props> = ({ config, onConfigChange }) => {
  const dispatch = useAppDispatch();
  const { me } = useAppSelector((s) => s.auth);
  const cookies = new Cookies();

  const [enabled, setEnabled] = useState<boolean>(me.download_audio);
  const [dailyEmails, setDailyEmails] = useState<{ value: string }[]>([]);

  const isImpersonated = !!cookies.get("gls-agency-token");

  const isAgencyUser = me.agency?.id && !me.is_agency_admin && !isImpersonated;

  useEffect(() => {
    setEnabled(me.download_audio);
    setDailyEmails(config.daily_recap_emails.map((e: string) => ({ value: e })));
  }, [me.download_audio, me.daily_recap_emails]);

  const handleToggle = async () => {
    const newVal = !enabled;
    setEnabled(newVal);

    await dispatch(usersUpdateUserNotAdmin({ id: me.id, data: { download_audio: newVal } }));
  };

  const handleEmailsChange = (updated: { value: string }[]) => {
    setDailyEmails(updated);
    const filtered = updated.filter((e) => e.value.trim() !== "");
    const list = filtered.map((e) => e.value);

    // Defer persistence to the global Save Changes button
    onConfigChange?.('daily_recap_emails', list);
  };
  
  return (
    <AppBox column gap={4} className="py-8 px-2 md:py-4 xl:py-6 xl:px-6">
      {/* Feature description */}
      <AppBox column gap={2} className="max-w-full">
        <AppText>
          Want to receive a daily recap email with AI summaries of your LSA Answered Calls?
        </AppText>
        <AppText variant="body">The daily recap email includes:</AppText>
        <ul className="list-disc list-inside text-sm space-y-1">
          <li>AI categorizes your calls: Lead Booked / Lead Lost / Lead Needs Followup / Lead Spam</li>
          <li>AI summaries: one-sentence overview plus the option for a longer, bullet-point summary</li>
          <li>Ability to text the caller, archive, or book inside LSA</li>
        </ul>
        <AppText className="mt-2">
          Please ask us about cost. Email us or book a call&nbsp;
          <a
            href="https://calendly.com/lsarespond/20min"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline"
          >HERE</a>.
        </AppText>
      </AppBox>
      
      {
        !isAgencyUser && 
        <>
          {/* Daily Recap Toggle */}
          <AppSwitch
            onChange={handleToggle}
            label={`Daily Recap Email is ${enabled ? "ON" : "OFF"}`}
            checked={enabled}
            className="w-full justify-center md:justify-start"
          />

          {/* Emails list */}
          <AppBox column width={334} gap={2}>
            {enabled && (
              <DynamicInputList
                labelPrefix="Daily Recap Email"
                maxInputs={4}
                initialValues={dailyEmails}
                onInputChange={handleEmailsChange}
              />
            )}
          </AppBox>
        </>
      }
    </AppBox>
  );
};

export default AnsweredCallReplies;